import React from 'react'
import { BaseWidgetProps, ConfigurablePropsFromDescription, type WidgetDescription } from '../../../helpers/widget'
import { useVoteToDisableUserActions } from '../../../redux/current-viewer'
import Area from '../../Area'
import { SparqlTriggerExecutor } from '../../SparqlTriggerExecutor'
import { ComponentNamespace, ComponentNamespaceContext, PublishedDict, usePubsub } from '../../../redux/pubsub'
import { VarMap, VarMapRecord } from './SetComponentConfig'
import { ValidComponentApp } from './ImportAppJSON'

const widgetOptions = [
  {
    label: 'app config to use as component',
    name: 'component',
    type: 'component-config',
  },
] as const satisfies WidgetDescription['variables']

export const componentWidgetDescriptor = {
  label: 'Component Widget [UNSTABLE]',
  name: 'Component', // without "Widget" because that's already added when hovering
  component: ComponentWidget,
  variables: widgetOptions,
} as const satisfies WidgetDescription

type WidgetProps
  = BaseWidgetProps
  & ConfigurablePropsFromDescription<typeof widgetOptions>

type LastApplied = {
  config: null | undefined | ValidComponentApp
  namespace: null | undefined | string
  parentVars: PublishedDict
  childVars: PublishedDict
  inputMapRecord: VarMapRecord
  outputMapRecord: VarMapRecord
}

export function ComponentWidget(props: WidgetProps) {
  const component = props.component != null && 'appRef' in props.component
    ? props.component
    : null

  if (!component?.appRef) return (
    <div>
      Provide an app config
    </div>
  )

  if (component.appRef.type === 'invalid') return (
    <div>
      Invalid config provided
    </div>
  )

  return (
    <ValidComponent {...props}
      parentNamespace={props.namespace}
      childNamespace={component.namespace}
      config={component.appRef.appConfig}
      inputMaps={component.varMapping.input ?? []}
      outputMaps={component.varMapping.output ?? []}
    />
  )
}

function ValidComponent(props: WidgetProps & {
  config: ValidComponentApp
  inputMaps: VarMap[]
  outputMaps: VarMap[]
  parentNamespace: ComponentNamespace
  childNamespace: string
}): React.ReactNode {
  const voteToDisableUserActions = useVoteToDisableUserActions()
  const [parentVars, publishToParent] = usePubsub(props.parentNamespace)
  const [childVars, publishToChild] = usePubsub(props.childNamespace)
  const lastApplied = React.useRef<LastApplied>({
    config: null,
    namespace: props.childNamespace,
    childVars: {},
    parentVars: {},
    inputMapRecord: {},
    outputMapRecord: {},
  })

  const initComponentVars = React.useCallback(() => {
    if (props.config.parameterValues) {
      const initComponentVars = JSON.parse(props.config.parameterValues) as Record<string, string>
      const actionId = `component ${props.childNamespace} -- init component vars`
      for (const key in initComponentVars) {
        publishToChild(key, initComponentVars[key], actionId)
      }
    }
  }, [props.config.parameterValues, publishToChild, props.childNamespace])

  const resetMappings = React.useCallback(() => {
    lastApplied.current.inputMapRecord = {}
    lastApplied.current.outputMapRecord = {}
  }, [])

  const mapInputVars = React.useCallback(() => {

    for (const [sourceName, targetName] of props.inputMaps) {
      if (!sourceName || !targetName) continue // in case of empty strings
      if (!(sourceName in parentVars)) continue

      let sourceRecord = lastApplied.current.inputMapRecord[sourceName]
      if (!sourceRecord) sourceRecord = lastApplied.current.inputMapRecord[sourceName] = {}
      if (sourceRecord[targetName] === parentVars[sourceName]) continue

      sourceRecord[targetName] = parentVars[sourceName]
      const actionId = `component ${props.childNamespace} -- map variable from host to component-input`
      publishToChild(targetName, parentVars[sourceName], actionId)
    }

  }, [props.inputMaps, parentVars, publishToChild])

  const mapOutputVars = React.useCallback(() => {

    for (const [sourceName, targetName] of props.outputMaps) {
      if (!sourceName || !targetName) continue // in case of empty strings
      if (!(sourceName in childVars)) continue

      let sourceRecord = lastApplied.current.outputMapRecord[sourceName]
      if (!sourceRecord) sourceRecord = lastApplied.current.outputMapRecord[sourceName] = {}
      if (sourceRecord[targetName] === childVars[sourceName]) continue

      sourceRecord[targetName] = childVars[sourceName]
      const actionId = `component ${props.childNamespace} -- map variable from component-output to host`
      publishToParent(targetName, childVars[sourceName], actionId)
    }

  }, [props.outputMaps, childVars, publishToParent, props.childNamespace])

  React.useEffect(() => {

    if (lastApplied.current.namespace !== props.childNamespace) {
      lastApplied.current.namespace = props.childNamespace
      console.log('component: applying new namespace')
      resetMappings()
      initComponentVars()
    }

    if (lastApplied.current.config !== props.config) {
      lastApplied.current.config = props.config
      console.log('component: applying new config')
      initComponentVars()
    }

    if (lastApplied.current.parentVars !== parentVars) {
      lastApplied.current.parentVars = parentVars
      mapInputVars()
    }

    if (lastApplied.current.childVars !== childVars) {
      lastApplied.current.childVars = childVars
      mapOutputVars()
    }

  }, [
    initComponentVars, resetMappings, mapInputVars, mapOutputVars, // effects
    props.config, props.childNamespace, parentVars, childVars, // dependent state
  ])

  return (
    <ComponentNamespaceContext.Provider value={props.childNamespace}>
      {props.config.triggers?.map(trigger => (
        <SparqlTriggerExecutor
          signalIsFetching={(isFetching) => {
            if (trigger.voteForUserActionsDisabledWhenLoading)
              voteToDisableUserActions(trigger.label, isFetching)
          }}
          key={trigger.label}
          templateId={trigger.label}
          sparqlTemplate={trigger.sparqlQuery}
        />))}
        {undefined}
      <Area {...props}
        notEditable
        config={props.config.mainArea}
        index={0}
        namespace={props.childNamespace}
      />
    </ComponentNamespaceContext.Provider>
  )
}
